/**
 * PortalScoringConfig — Admin page for tuning scoring engine weights
 *
 * Persists configuration to the `scoring_config` table in Supabase (per user_id).
 * Also keeps localStorage in sync as a fast cache.
 */

import React, { useState, useEffect, useCallback } from 'react';
import PortalLayout from '@/components/portal/PortalLayout';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Sliders, RotateCcw, Save, Loader2 } from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { supabaseGet, supabasePost, supabasePatch } from '@/utils/supabaseRest';

// ── Storage key ───────────────────────────────────────────────────────
const STORAGE_KEY = 'intentdrive_scoring_config';

// ── Default values ────────────────────────────────────────────────────

interface ScoringWeights {
  intent_match: number;
  qualification: number;
  revenue_payout: number;
  conversion_probability: number;
  cross_sell_potential: number;
  compliance_risk_penalty: number;
  fatigue_repetition_penalty: number;
}

interface PhaseThresholds {
  rejections_phase_2: number;
  rejections_phase_3: number;
  rejections_phase_4: number;
}

interface FatiguePenalties {
  last_shown_penalty: number;
  rejected_penalty: number;
  previously_shown_penalty: number;
  low_engagement_penalty: number;
}

interface ScoringConfig {
  weights: ScoringWeights;
  phases: PhaseThresholds;
  fatigue: FatiguePenalties;
}

const DEFAULT_CONFIG: ScoringConfig = {
  weights: {
    intent_match: 30,
    qualification: 20,
    revenue_payout: 20,
    conversion_probability: 15,
    cross_sell_potential: 10,
    compliance_risk_penalty: 15,
    fatigue_repetition_penalty: 10,
  },
  phases: {
    rejections_phase_2: 1,
    rejections_phase_3: 2,
    rejections_phase_4: 3,
  },
  fatigue: {
    last_shown_penalty: -100,
    rejected_penalty: -15,
    previously_shown_penalty: -5,
    low_engagement_penalty: -8,
  },
};

// ── Weight slider metadata ────────────────────────────────────────────

const WEIGHT_SLIDERS: { key: keyof ScoringWeights; label: string }[] = [
  { key: 'intent_match', label: 'Intent Match Weight' },
  { key: 'qualification', label: 'Qualification Weight' },
  { key: 'revenue_payout', label: 'Revenue / Payout Weight' },
  { key: 'conversion_probability', label: 'Conversion Probability Weight' },
  { key: 'cross_sell_potential', label: 'Cross-Sell Potential Weight' },
  { key: 'compliance_risk_penalty', label: 'Compliance Risk Penalty' },
  { key: 'fatigue_repetition_penalty', label: 'Fatigue / Repetition Penalty' },
];

const PHASE_FIELDS: { key: keyof PhaseThresholds; label: string }[] = [
  { key: 'rejections_phase_2', label: 'Rejections before Phase 2' },
  { key: 'rejections_phase_3', label: 'Rejections before Phase 3' },
  { key: 'rejections_phase_4', label: 'Rejections before Phase 4' },
];

const FATIGUE_FIELDS: { key: keyof FatiguePenalties; label: string }[] = [
  { key: 'last_shown_penalty', label: 'Last Shown Penalty' },
  { key: 'rejected_penalty', label: 'Rejected Penalty' },
  { key: 'previously_shown_penalty', label: 'Previously Shown Penalty' },
  { key: 'low_engagement_penalty', label: 'Low Engagement Penalty' },
];

// ── DB row shape ────────────────────────────────────────────────────

interface ScoringConfigRow {
  id: string;
  user_id: string;
  weights: { intent?: number; qualification?: number; revenue?: number; conversion?: number; cross_sell?: number; compliance?: number; fatigue?: number };
  phase_thresholds: { phase2_rejections?: number; phase3_rejections?: number; phase4_rejections?: number };
  fatigue_penalties: { last_shown?: number; rejected?: number; shown?: number; low_engagement?: number };
}

/** Map the flat portal-side ScoringConfig to the DB JSONB columns */
function toDbPayload(cfg: ScoringConfig, userId: string) {
  return {
    user_id: userId,
    weights: {
      intent: cfg.weights.intent_match,
      qualification: cfg.weights.qualification,
      revenue: cfg.weights.revenue_payout,
      conversion: cfg.weights.conversion_probability,
      cross_sell: cfg.weights.cross_sell_potential,
      compliance: cfg.weights.compliance_risk_penalty,
      fatigue: cfg.weights.fatigue_repetition_penalty,
    },
    phase_thresholds: {
      phase2_rejections: cfg.phases.rejections_phase_2,
      phase3_rejections: cfg.phases.rejections_phase_3,
      phase4_rejections: cfg.phases.rejections_phase_4,
    },
    fatigue_penalties: {
      last_shown: cfg.fatigue.last_shown_penalty,
      rejected: cfg.fatigue.rejected_penalty,
      shown: cfg.fatigue.previously_shown_penalty,
      low_engagement: cfg.fatigue.low_engagement_penalty,
    },
  };
}

/** Map a DB row back to the portal-side ScoringConfig */
function fromDbRow(row: ScoringConfigRow): ScoringConfig {
  return {
    weights: {
      intent_match: row.weights.intent ?? DEFAULT_CONFIG.weights.intent_match,
      qualification: row.weights.qualification ?? DEFAULT_CONFIG.weights.qualification,
      revenue_payout: row.weights.revenue ?? DEFAULT_CONFIG.weights.revenue_payout,
      conversion_probability: row.weights.conversion ?? DEFAULT_CONFIG.weights.conversion_probability,
      cross_sell_potential: row.weights.cross_sell ?? DEFAULT_CONFIG.weights.cross_sell_potential,
      compliance_risk_penalty: row.weights.compliance ?? DEFAULT_CONFIG.weights.compliance_risk_penalty,
      fatigue_repetition_penalty: row.weights.fatigue ?? DEFAULT_CONFIG.weights.fatigue_repetition_penalty,
    },
    phases: {
      rejections_phase_2: row.phase_thresholds.phase2_rejections ?? DEFAULT_CONFIG.phases.rejections_phase_2,
      rejections_phase_3: row.phase_thresholds.phase3_rejections ?? DEFAULT_CONFIG.phases.rejections_phase_3,
      rejections_phase_4: row.phase_thresholds.phase4_rejections ?? DEFAULT_CONFIG.phases.rejections_phase_4,
    },
    fatigue: {
      last_shown_penalty: row.fatigue_penalties.last_shown ?? DEFAULT_CONFIG.fatigue.last_shown_penalty,
      rejected_penalty: row.fatigue_penalties.rejected ?? DEFAULT_CONFIG.fatigue.rejected_penalty,
      previously_shown_penalty: row.fatigue_penalties.shown ?? DEFAULT_CONFIG.fatigue.previously_shown_penalty,
      low_engagement_penalty: row.fatigue_penalties.low_engagement ?? DEFAULT_CONFIG.fatigue.low_engagement_penalty,
    },
  };
}

// ── Component ─────────────────────────────────────────────────────────

export default function PortalScoringConfig() {
  const { user } = useAuth();
  const userId = user?.id;
  const [config, setConfig] = useState<ScoringConfig>({ ...DEFAULT_CONFIG });
  const [saved, setSaved] = useState(false);
  const [loading, setLoading] = useState(true);
  const [hasDbRow, setHasDbRow] = useState(false);
  const [saving, setSaving] = useState(false);

  // Load config from DB on mount
  useEffect(() => {
    if (!userId) { setLoading(false); return; }
    (async () => {
      try {
        const rows = await supabaseGet<ScoringConfigRow[]>(
          `scoring_config?select=*&user_id=eq.${userId}&limit=1`
        );
        if (rows && rows.length > 0) {
          setConfig(fromDbRow(rows[0]));
          setHasDbRow(true);
        }
      } catch (err) {
        console.warn('Failed to load scoring config from DB, using defaults:', err);
      } finally {
        setLoading(false);
      }
    })();
  }, [userId]);

  const totalWeight = Object.values(config.weights).reduce((a, b) => a + b, 0);

  const updateWeight = useCallback((key: keyof ScoringWeights, value: number) => {
    setConfig((prev) => ({
      ...prev,
      weights: { ...prev.weights, [key]: value },
    }));
    setSaved(false);
  }, []);

  const updatePhase = useCallback((key: keyof PhaseThresholds, value: number) => {
    setConfig((prev) => ({
      ...prev,
      phases: { ...prev.phases, [key]: value },
    }));
    setSaved(false);
  }, []);

  const updateFatigue = useCallback((key: keyof FatiguePenalties, value: number) => {
    setConfig((prev) => ({
      ...prev,
      fatigue: { ...prev.fatigue, [key]: value },
    }));
    setSaved(false);
  }, []);

  const handleSave = async () => {
    if (!userId) return;
    setSaving(true);
    try {
      const payload = toDbPayload(config, userId);
      if (hasDbRow) {
        await supabasePatch(`scoring_config?user_id=eq.${userId}`, payload);
      } else {
        await supabasePost('scoring_config', payload);
        setHasDbRow(true);
      }
      // Also keep localStorage in sync as a fast cache
      localStorage.setItem(STORAGE_KEY, JSON.stringify(config));
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save scoring config:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleReset = () => {
    setConfig({ ...DEFAULT_CONFIG });
    setSaved(false);
  };

  return (
    <PortalLayout>
      <div className="p-6 md:p-10 max-w-4xl mx-auto space-y-8">
        {/* Page header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="h-10 w-10 rounded-lg bg-emerald-500/10 flex items-center justify-center">
              <Sliders className="h-5 w-5 text-emerald-400" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-white">Scoring Configuration</h1>
              <p className="text-sm text-slate-400">Tune how the scoring engine ranks verticals</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Button
              variant="outline"
              className="border-slate-700 text-slate-300 hover:bg-slate-800 hover:text-white"
              onClick={handleReset}
            >
              <RotateCcw className="h-4 w-4 mr-2" />
              Reset to Defaults
            </Button>
            <Button
              className="bg-emerald-500 hover:bg-emerald-600 text-white"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              {saved ? 'Saved!' : saving ? 'Saving...' : 'Save Weights'}
            </Button>
          </div>
        </div>

        {loading && (
          <div className="flex items-center justify-center py-12 text-slate-400">
            <Loader2 className="h-5 w-5 animate-spin mr-2" />
            Loading configuration...
          </div>
        )}

        {!loading && <>
        {/* Total weight banner */}
        <div
          className={`rounded-lg border px-4 py-3 text-sm font-medium ${
            totalWeight === 100
              ? 'border-emerald-500/30 bg-emerald-500/10 text-emerald-400'
              : 'border-amber-500/30 bg-amber-500/10 text-amber-400'
          }`}
        >
          Total Weight: {totalWeight} / 100
          {totalWeight !== 100 && ' — weights do not sum to 100'}
        </div>

        {/* ── Scoring Weights ─────────────────────────────────── */}
        <Card className="border-slate-800 bg-slate-900">
          <CardHeader>
            <CardTitle className="text-lg text-white">Scoring Weights</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {WEIGHT_SLIDERS.map(({ key, label }) => (
              <div key={key} className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm text-slate-300">{label}</label>
                  <span className="text-sm font-mono text-emerald-400 w-8 text-right">
                    {config.weights[key]}
                  </span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={50}
                  value={config.weights[key]}
                  onChange={(e) => updateWeight(key, Number(e.target.value))}
                  className="w-full h-2 rounded-full appearance-none cursor-pointer bg-slate-700 accent-emerald-500
                    [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                    [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-emerald-500
                    [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-emerald-500/30"
                />
                <div className="flex justify-between text-[10px] text-slate-600">
                  <span>0</span>
                  <span>25</span>
                  <span>50</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* ── Phase Transition Thresholds ─────────────────────── */}
        <Card className="border-slate-800 bg-slate-900">
          <CardHeader>
            <CardTitle className="text-lg text-white">Phase Transition Thresholds</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {PHASE_FIELDS.map(({ key, label }) => (
              <div key={key} className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm text-slate-300">{label}</label>
                  <span className="text-sm font-mono text-emerald-400 w-8 text-right">
                    {config.phases[key]}
                  </span>
                </div>
                <input
                  type="range"
                  min={0}
                  max={10}
                  value={config.phases[key]}
                  onChange={(e) => updatePhase(key, Number(e.target.value))}
                  className="w-full h-2 rounded-full appearance-none cursor-pointer bg-slate-700 accent-emerald-500
                    [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                    [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-emerald-500
                    [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-emerald-500/30"
                />
                <div className="flex justify-between text-[10px] text-slate-600">
                  <span>0</span>
                  <span>5</span>
                  <span>10</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>

        {/* ── Fatigue Penalties ────────────────────────────────── */}
        <Card className="border-slate-800 bg-slate-900">
          <CardHeader>
            <CardTitle className="text-lg text-white">Fatigue Penalties</CardTitle>
          </CardHeader>
          <CardContent className="space-y-6">
            {FATIGUE_FIELDS.map(({ key, label }) => (
              <div key={key} className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-sm text-slate-300">{label}</label>
                  <span className="text-sm font-mono text-red-400 w-12 text-right">
                    {config.fatigue[key]}
                  </span>
                </div>
                <input
                  type="range"
                  min={-100}
                  max={0}
                  value={config.fatigue[key]}
                  onChange={(e) => updateFatigue(key, Number(e.target.value))}
                  className="w-full h-2 rounded-full appearance-none cursor-pointer bg-slate-700 accent-red-500
                    [&::-webkit-slider-thumb]:appearance-none [&::-webkit-slider-thumb]:w-4 [&::-webkit-slider-thumb]:h-4
                    [&::-webkit-slider-thumb]:rounded-full [&::-webkit-slider-thumb]:bg-red-500
                    [&::-webkit-slider-thumb]:shadow-lg [&::-webkit-slider-thumb]:shadow-red-500/30"
                />
                <div className="flex justify-between text-[10px] text-slate-600">
                  <span>-100</span>
                  <span>-50</span>
                  <span>0</span>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
        </>}
      </div>
    </PortalLayout>
  );
}
